import { toast } from '@blocksuite/affine-components/toast';
import { isCatalogImage } from '@blocksuite/affine-model';
import {
  createBlockIndexReviewLocator,
  EmbeddingBadgeController,
  type EmbeddingBadgeControllerHost,
  embeddingBadgeStyles,
  type EmbeddingIndexReviewLocator,
} from '@blocksuite/affine-shared/services';
import type { BlockComponent } from '@blocksuite/std';
import { css, nothing } from 'lit';

type ImageEmbeddingStatusHost = Pick<
  BlockComponent,
  'host' | 'model' | 'querySelector' | 'requestUpdate' | 'std' | 'store'
>;

// Image-specific chrome: the shared embedding badge plus a hide-until-image-hover
// gate (the badge only appears while the image is hovered, or while it is
// loading / its details popover is open).
//
// IMPORTANT: BlockSuite block components are shadowless — their `static styles`
// are injected into the GLOBAL document, not a shadow root. The hover-gate must
// therefore be scoped to `.resizable-img` (the image wrapper); a bare
// `.affine-embedding-badge` selector would hide EVERY badge in the app
// (attachment, PDF viewer) that is not inside a hovered image.
export const imageEmbeddingBadgeStyles = css`
  ${embeddingBadgeStyles}

  .resizable-img {
    --affine-embedding-badge-inline-max-width: calc(100% - 16px);
  }

  .resizable-img .affine-embedding-badge {
    opacity: 0;
    pointer-events: none;
  }

  .resizable-img:hover .affine-embedding-badge,
  .resizable-img .affine-embedding-badge.loading,
  .resizable-img .affine-embedding-badge.expanded,
  .resizable-img .affine-embedding-badge.review-open {
    opacity: 1;
    pointer-events: auto;
  }
`;

export class ImageEmbeddingStatusController {
  private readonly badge: EmbeddingBadgeController;

  constructor(private readonly owner: ImageEmbeddingStatusHost) {
    this.badge = new EmbeddingBadgeController(
      owner as unknown as EmbeddingBadgeControllerHost,
      {
        notify: message => toast(owner.host, message),
        getIndexReviewLocator: () => imageIndexReviewLocator(owner),
        getPortalAnchor: () =>
          owner.querySelector<HTMLElement>('.resizable-img'),
        mediaType: () => null,
      }
    );
  }

  connect() {
    this.badge.connect();
  }

  disconnect() {
    this.badge.disconnect();
  }

  refresh() {
    this.badge.refresh();
  }

  render() {
    // Catalogue art is never indexed, so it carries no index status.
    if (isCatalogImage(this.owner.model.props)) return nothing;
    return this.badge.render();
  }
}

function imageIndexReviewLocator(
  owner: ImageEmbeddingStatusHost
): EmbeddingIndexReviewLocator | null {
  const props = owner.model.props as {
    sourceId?: string;
    sourceId$?: { value?: string };
  };
  const blobId = props.sourceId$?.value ?? props.sourceId ?? null;
  return createBlockIndexReviewLocator({
    workspaceId: owner.store.workspace.id,
    docId: owner.store.id,
    subjectKind: 'image-block',
    blockId: owner.model.id,
    blobId,
  });
}
